// src/pages/RepDashboardPage.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box, Heading, HStack, Text, Select, Input, InputGroup, InputLeftElement,
  Badge, Button, Table, Thead, Tbody, Tr, Th, Td, Stat, StatLabel, StatNumber,
  StatHelpText, Stack, Tooltip, useToast, Tag, TagLabel, Spinner,
  Tabs, TabList, TabPanels, Tab, TabPanel
} from "@chakra-ui/react";
import { SearchIcon, DownloadIcon, InfoOutlineIcon } from "@chakra-ui/icons";
import { useCognito } from "../utils/useCognito";
import NavAccessProbe from "../components/NavAccessProbe";
import RepManageTab from "../components/RepManageTab";

const API_BASE = "https://w6mxt54h5f.execute-api.us-east-2.amazonaws.com/Prod/api";

// ---------- Helpers ----------
const fmt = (n) =>
  n?.toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 });

const toDate = (s) => (s ? new Date(s) : null);
const addDays = (d, days) => { const dt = new Date(d); dt.setDate(dt.getDate() + days); return dt; };
const etaFromPaid = (paidAt, termsDays) => (paidAt ? addDays(new Date(paidAt), termsDays) : null);

const within = (d, from, to) => {
  if (!d) return false;
  const t = d.getTime();
  if (from && t < from.getTime()) return false;
  if (to && t > to.getTime()) return false;
  return true;
};

// role helper
const getRole = (user) => {
  const rolesClaim =
    user?.["https://admin.curagenesis.com/roles"] ||
    user?.["https://curagenesis.com/roles"] ||
    user?.roles || [];
  return Array.isArray(rolesClaim) ? (rolesClaim[0] || "rep") : (rolesClaim || "rep");
};

async function fetchJson(url, token) {
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status} ${res.statusText}: ${text}`);
  }
  return res.json();
}

// ---------- Component ----------
const RepDashboardPage = () => {
  const { user, getIdToken } = useCognito();
  const toast = useToast();

  const [role, setRole] = useState("rep");

  // Admin rep list + selection
  const [reps, setReps] = useState([]); // [{repId,name,email,repCode}]
  const [selectedRepId, setSelectedRepId] = useState("");

  // Dashboard data
  const [repHeader, setRepHeader] = useState(null); // {repId,name,email,repCode,commissionRatePct,payoutTermsDays}
  const [accounts, setAccounts] = useState([]);     // [{accountId, accountName}]
  const [orders, setOrders] = useState([]);         // [...]
  const [loading, setLoading] = useState(true);
  const [loadingRepData, setLoadingRepData] = useState(false);

  // Filters
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // --- helpers (admin) ---
  const loadReps = useCallback(async () => {
    const token = getIdToken();
    const repsResp = await fetchJson(`${API_BASE}/reps?limit=200`, token);
    const list = repsResp.reps || [];
    setReps(list);
    return list;
  }, [getIdToken]);

  const loadDashboardForRep = useCallback(
    async (repId) => {
      const token = getIdToken();
      const dash = await fetchJson(
        `${API_BASE}/rep-dashboard${repId ? `?repId=${encodeURIComponent(repId)}` : ""}`,
        token
      );
      setRepHeader(dash.rep || null);
      setAccounts(dash.accounts || []);
      setOrders(dash.orders || []);
      return dash;
    },
    [getIdToken]
  );

  // INIT: role + initial data
  useEffect(() => {
    (async () => {
      try {
        if (!API_BASE) {
          console.warn("[RepDashboard] No API base configured (REACT_APP_ADMIN_API_URL).");
        }
        const r = getRole(user || {});
        setRole(r);

        if (r === "admin") {
          const list = await loadReps();
          const firstRepId = list?.[0]?.repId || "";
          setSelectedRepId(firstRepId);
          if (firstRepId) {
            await loadDashboardForRep(firstRepId);
          }
        } else {
          await loadDashboardForRep(null); // server derives rep from token
          setSelectedRepId((prev) => prev || repHeader?.repId || "");
        }
      } catch (e) {
        console.error("[RepDashboard] init error:", e);
        toast({
          title: "Failed to load dashboard",
          description: e.message,
          status: "error",
          isClosable: true,
        });
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Admin: re-fetch dashboard when selection changes
  useEffect(() => {
    if (role !== "admin") return;
    if (!selectedRepId) return;

    let cancelled = false;
    (async () => {
      try {
        setLoadingRepData(true);
        const dash = await loadDashboardForRep(selectedRepId);
        if (!cancelled) {
          setRepHeader(dash.rep || null);
          setAccounts(dash.accounts || []);
          setOrders(dash.orders || []);
        }
      } catch (e) {
        if (!cancelled) {
          console.error("[RepDashboard] reload error:", e);
          toast({ title: "Failed to load rep data", description: e.message, status: "error", isClosable: true });
        }
      } finally {
        if (!cancelled) setLoadingRepData(false);
      }
    })();

    return () => { cancelled = true; };
  }, [role, selectedRepId, loadDashboardForRep, toast]);

  // Index account names
  const accountsById = useMemo(() => {
    const m = {};
    (accounts || []).forEach((a) => (m[a.accountId] = a.accountName));
    return m;
  }, [accounts]);

  // Filters
  const qLower = q.trim().toLowerCase();
  const fromDt = dateFrom ? new Date(dateFrom) : null;
  const toDt = dateTo ? new Date(dateTo) : null;

  const filtered = useMemo(() => {
    return (orders || []).filter((o) => {
      if (qLower) {
        const fields = [
          o.orderId, o.patientName, accountsById[o.accountId], o.invoiceId, o.status, o.notes
        ].filter(Boolean).join(" ").toLowerCase();
        if (!fields.includes(qLower)) return false;
      }
      if (statusFilter === "paid" && !o.paid) return false;
      if (statusFilter === "unpaid" && o.paid) return false;

      if (fromDt || toDt) {
        const pd = toDate(o.paidAt);
        if (o.paid && !within(pd, fromDt, toDt)) return false;
      }
      return true;
    });
  }, [orders, qLower, statusFilter, fromDt, toDt, accountsById]);

  // Derived totals
  const commissionRate = repHeader?.commissionRatePct ?? 0;
  const termsDays = repHeader?.payoutTermsDays ?? 30;

  const kpis = useMemo(() => {
    let paidCount = 0, unpaidCount = 0, paidTotal = 0, repCutAccrued = 0, repCutPending = 0;
    (orders || []).forEach((o) => {
      const cutPct = (o.cutOverridePct ?? commissionRate) / 100.0;
      const cut = (o.invoiceTotal || 0) * cutPct;
      if (o.paid) { paidCount++; paidTotal += o.invoiceTotal || 0; repCutAccrued += cut; }
      else { unpaidCount++; repCutPending += cut; }
    });
    return { paidCount, unpaidCount, paidTotal, repCutAccrued, repCutPending, allOrders: (orders || []).length };
  }, [orders, commissionRate]);

  // Export CSV
  const exportCsv = () => {
    const headers = [
      "Order ID","Account","Patient","Status","Paid","Paid At","Invoice ID","Invoice Total",
      "Rep Cut %","Rep Cut $","Payout ETA","Notes"
    ];
    const rows = filtered.map((o) => {
      const cutPct = (o.cutOverridePct ?? commissionRate);
      const cutAmt = ((o.invoiceTotal || 0) * (cutPct / 100));
      const eta = o.paid ? etaFromPaid(o.paidAt, termsDays) : null;
      return [
        o.orderId, (accountsById[o.accountId] || o.accountId), (o.patientName || ""),
        (o.status || ""), (o.paid ? "Yes" : "No"), (o.paidAt || ""),
        (o.invoiceId || ""), (o.invoiceTotal?.toFixed(2) || "0.00"),
        cutPct?.toFixed(2), cutAmt.toFixed(2), (eta ? eta.toISOString() : ""),
        (o.notes || "").replace(/\n/g, " ")
      ];
    });

    const csv = [headers, ...rows].map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rep-${repHeader?.repId || "export"}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Callback from Manage tab when a new rep is created
  const handleRepCreated = useCallback(async (resp) => {
    try {
      const list = await loadReps();
      // try to select the newly created rep if ID is returned
      const newId = resp?.repId;
      if (newId && list.some(r => r.repId === newId)) {
        setSelectedRepId(newId);
      }
      // refresh dashboard for the currently selected rep
      if (role === "admin") {
        await loadDashboardForRep(newId || selectedRepId);
      }
    } catch (e) {
      // non-fatal
      console.warn("Post-create refresh failed:", e);
    }
  }, [loadReps, loadDashboardForRep, role, selectedRepId]);

  if (loading) {
    return (
      <Box p={[4,6,8]}>
        <HStack justify="space-between" align="center" mb={4}>
          <Heading size="lg">Sales Rep Dashboard</Heading>
          <Tag colorScheme="gray"><TagLabel>Loading…</TagLabel></Tag>
        </HStack>
        <Spinner size="lg" />
      </Box>
    );
  }

  // ---- dashboard panel content extracted so we can reuse in a TabPanel ----
  const DashboardPanel = (
    <>
      {/* Admin controls */}
      {role === "admin" && (
        <Box borderWidth="1px" borderRadius="lg" p={4} mb={6} bg="gray.50">
          <HStack spacing={3} align="end" flexWrap="wrap">
            <Box minW="260px">
              <Text fontSize="sm" mb={1}>Select Rep</Text>
              <Select
                value={selectedRepId}
                onChange={(e) => setSelectedRepId(e.target.value)}
                _hover={{ transform: "scale(1)" }}
              >
                {reps.map((r) => (
                  <option key={r.repId} value={r.repId}>
                    {r.name || `${r.firstName ?? ""} ${r.lastName ?? ""}`.trim() || r.email} — {r.repCode || r.repId}
                  </option>
                ))}
              </Select>
            </Box>
            <Box minW="260px">
              <Text fontSize="sm" mb={1}>Quick Search Reps</Text>
              <Input
                placeholder="Type name, code, or email…"
                onChange={(e) => {
                  const term = e.target.value.toLowerCase().trim();
                  if (!term) return;
                  const hit = reps.find(
                    (r) =>
                      ((r.name || `${r.firstName ?? ""} ${r.lastName ?? ""}`) || "")
                        .toLowerCase().includes(term) ||
                      (r.repCode || "").toLowerCase().includes(term) ||
                      (r.email || "").toLowerCase().includes(term)
                  );
                  if (hit) setSelectedRepId(hit.repId);
                }}
                _hover={{ transform: "scale(1)" }}
              />
            </Box>
            {loadingRepData && <Spinner size="sm" ml={2} />}
          </HStack>
        </Box>
      )}

      {/* Rep header */}
      {repHeader && (
        <Box mb={4}>
          <HStack spacing={4} align="center" flexWrap="wrap">
            <Text fontWeight="semibold">{repHeader.name}</Text>
            <Text color="gray.600">{repHeader.email}</Text>
            <Badge colorScheme="teal">{repHeader.repCode}</Badge>
          </HStack>
          <Text fontSize="sm" color="gray.600">
            Default commission: <strong>{repHeader.commissionRatePct}%</strong> • Payout terms:{" "}
            <strong>Net {repHeader.payoutTermsDays}</strong>
          </Text>
        </Box>
      )}

      {/* KPIs */}
      <Stack direction={{ base: "column", md: "row" }} spacing={4} mb={6}>
        <Stat borderWidth="1px" borderRadius="lg" p={4}>
          <StatLabel>Total Orders</StatLabel>
          <StatNumber>{kpis.allOrders}</StatNumber>
          <StatHelpText>{kpis.paidCount} paid • {kpis.unpaidCount} unpaid</StatHelpText>
        </Stat>
        <Stat borderWidth="1px" borderRadius="lg" p={4}>
          <StatLabel>Paid Invoice Total</StatLabel>
          <StatNumber>{fmt(kpis.paidTotal)}</StatNumber>
          <StatHelpText>Sum of fully paid orders</StatHelpText>
        </Stat>
        <Stat borderWidth="1px" borderRadius="lg" p={4}>
          <StatLabel>Rep Cut (Accrued)</StatLabel>
          <StatNumber>{fmt(kpis.repCutAccrued)}</StatNumber>
          <StatHelpText>Expected in next payouts</StatHelpText>
        </Stat>
        <Stat borderWidth="1px" borderRadius="lg" p={4}>
          <StatLabel>Rep Cut (Pending)</StatLabel>
          <StatNumber>{fmt(kpis.repCutPending)}</StatNumber>
          <StatHelpText>Unpaid invoices</StatHelpText>
        </Stat>
      </Stack>

      {/* Filters */}
      <Box borderWidth="1px" borderRadius="lg" p={4} mb={4}>
        <HStack spacing={3} flexWrap="wrap">
          <InputGroup maxW="280px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search orders, patients, accounts, invoices…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              _hover={{ transform: "scale(1)" }}
            />
          </InputGroup>

          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            maxW="180px"
            _hover={{ transform: "scale(1)" }}
          >
            <option value="all">All</option>
            <option value="paid">Paid</option>
            <option value="unpaid">Unpaid</option>
          </Select>

          <HStack>
            <Box>
              <Text fontSize="xs" color="gray.600">Paid From</Text>
              <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} _hover={{ transform: "scale(1)" }} />
            </Box>
            <Box>
              <Text fontSize="xs" color="gray.600">Paid To</Text>
              <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} _hover={{ transform: "scale(1)" }} />
            </Box>
          </HStack>

          <Button leftIcon={<DownloadIcon />} variant="outline" onClick={exportCsv} _hover={{ transform: "scale(1)" }}>
            Export CSV
          </Button>
        </HStack>
      </Box>

      {/* Table */}
      <Box borderWidth="1px" borderRadius="lg" overflowX="auto">
        <Table size="sm" variant="striped">
          <Thead>
            <Tr>
              <Th>Order</Th>
              <Th>Account</Th>
              <Th>Patient</Th>
              <Th>Status</Th>
              <Th>Paid</Th>
              <Th>Paid At</Th>
              <Th isNumeric>Invoice</Th>
              <Th isNumeric>Rep %</Th>
              <Th isNumeric>Rep $</Th>
              <Th>Payout ETA</Th>
              <Th>Notes</Th>
            </Tr>
          </Thead>
          <Tbody>
            {filtered.map((o) => {
              const cutPct = (o.cutOverridePct ?? commissionRate);
              const cutAmt = ((o.invoiceTotal || 0) * (cutPct / 100));
              const eta = o.paid ? etaFromPaid(o.paidAt, termsDays) : null;

              return (
                <Tr key={o.orderId}>
                  <Td>
                    <HStack spacing={2}>
                      <Text fontWeight="semibold">{o.orderId}</Text>
                      {o.invoiceId && (
                        <Tooltip label={`Invoice: ${o.invoiceId}`} hasArrow>
                          <InfoOutlineIcon aria-label="invoice" />
                        </Tooltip>
                      )}
                    </HStack>
                  </Td>
                  <Td>{accountsById[o.accountId] || o.accountId}</Td>
                  <Td>{o.patientName || "—"}</Td>
                  <Td><Badge colorScheme="purple">{o.status || "—"}</Badge></Td>
                  <Td>{o.paid ? <Badge colorScheme="green">Yes</Badge> : <Badge colorScheme="red">No</Badge>}</Td>
                  <Td>{o.paidAt ? new Date(o.paidAt).toLocaleDateString() : "—"}</Td>
                  <Td isNumeric>{fmt(o.invoiceTotal || 0)}</Td>
                  <Td isNumeric>{cutPct?.toFixed(2)}%</Td>
                  <Td isNumeric>{fmt(cutAmt)}</Td>
                  <Td>{eta ? eta.toLocaleDateString() : "—"}</Td>
                  <Td maxW="260px" whiteSpace="normal">{o.notes || "—"}</Td>
                </Tr>
              );
            })}
            {filtered.length === 0 && (
              <Tr>
                <Td colSpan={11}>
                  <Box p={6} textAlign="center" color="gray.500">No matching records.</Box>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    </>
  );

  return (
    <Box p={[4,6,8]}>
      <HStack justify="space-between" align="center" mb={4}>
        <Heading size="lg">Sales Rep Dashboard</Heading>
        <Tag colorScheme={role === "admin" ? "purple" : "blue"}>
          <TagLabel>{role === "admin" ? "Admin View" : "Rep View"}</TagLabel>
        </Tag>
      </HStack>

      {role === "admin" ? (
        <Tabs variant="enclosed">
          <TabList>
            <Tab _hover={{ transform: "scale(1)" }}>Overview</Tab>
            <Tab _hover={{ transform: "scale(1)" }}>Manage Reps</Tab>
          </TabList>
          <TabPanels mt={4}>
            <TabPanel px={0}>{DashboardPanel}</TabPanel>
            <TabPanel px={0}>
              <RepManageTab
                onRepCreated={handleRepCreated}
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      ) : (
        // Rep users only see the dashboard
        DashboardPanel
      )}

      {/* Footer hint */}
      <Text mt={3} fontSize="xs" color="gray.500">
        Powered by <code>/rep-dashboard</code>, <code>/reps</code>, <code>/facilities</code> (admin), and <code>/assign-rep-to-facility</code>.
      </Text>
      <NavAccessProbe />
    </Box>
  );
};

export default RepDashboardPage;
